import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { Entitlements } from '@snap-platform/shared';
import { useAuth } from '../context/AuthContext.js';
import { useSnap } from '../hooks/useSnap.js';
import { getEntitlements, getSubscriptionStatus } from '../services/api.js';
import { ShieldCheckIcon } from './Icons.js';

function StatusDot({ status }: { status: 'green' | 'yellow' | 'red' }) {
  return <span className={`psb-dot psb-dot-${status}`} aria-hidden="true" />;
}

export function ProtectionStatusBar() {
  const location = useLocation();
  const isHomepage = location.pathname === '/' || location.pathname === '';
  const { isAuthenticated, token } = useAuth();
  const snap = useSnap(token);
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [subStatus, setSubStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    getEntitlements(token).then(setEntitlements).catch(() => {});
    getSubscriptionStatus(token).then((s) => setSubStatus(s.status)).catch(() => {});
  }, [token]);

  // Do not show on homepage — preserves institutional authority
  if (isHomepage) return null;

  const isPro = entitlements?.tier === 'pro';
  const isSubActive = subStatus === 'active';
  const snapOk = snap.isInstalled;
  const fullyProtected = isPro && isSubActive && snapOk;

  // Determine state
  const state: 'green' | 'amber' | 'red' = !isAuthenticated
    ? 'red'
    : fullyProtected
      ? 'green'
      : 'amber';

  // Build message
  let message: string;
  if (!isAuthenticated) {
    message = 'Not Protected \u2014 Connect wallet to begin protection';
  } else if (fullyProtected) {
    message = 'Fully Protected \u2014 Your wallet is actively protected';
  } else if (!isPro) {
    message = 'Limited Protection \u2014 Upgrade to unlock full protection';
  } else if (!snapOk) {
    message = 'Protection Incomplete \u2014 Install Snap to activate protection';
  } else {
    message = 'Limited Protection \u2014 Complete setup for full protection';
  }

  return (
    <div className={`psb-container psb-${state}`}>
      <div className="psb-inner">
        <div className="psb-left">
          <ShieldCheckIcon className="psb-icon" aria-hidden="true" />
          <span className="psb-message">{message}</span>
        </div>
        <div className="psb-center">
          {isAuthenticated && (
            <>
              <span className="psb-status"><StatusDot status="green" /> Wallet</span>
              <span className="psb-status"><StatusDot status={isPro && isSubActive ? 'green' : 'red'} /> {isPro ? 'Pro' : 'Free'}</span>
              <span className="psb-status"><StatusDot status={snapOk ? 'green' : 'yellow'} /> Snap</span>
            </>
          )}
        </div>
        <div className="psb-right">
          {isAuthenticated ? (
            <>
              {!isPro && (
                <a className="psb-button" href="/pricing">Upgrade</a>
              )}
              {isPro && !snapOk && snap.isMetaMask && (
                <button className="psb-button" onClick={snap.installSnap} disabled={snap.isLoading}>
                  {snap.isLoading ? 'Installing...' : 'Install Snap'}
                </button>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
